import type { StatusDef, Transition } from '../../api/types';
import type { TransitionAdvice } from '../../api/endpoints';

/**
 * 🔴🔴 WHAT THE INLINE STATUS DROPDOWN MAY OFFER — the other half of PH-19.
 *
 * The board was not the only place that read `status_transition` as a permission list. This menu did
 * too, and the consequence is written down in `20260817-definition-of-done-review-gate.md`:
 *
 *   > *"I could not reopen it: `done` has no outgoing transitions, which is the very rule PH-19
 *   > removes."*
 *
 * A ticket in `done` had ZERO options here and the menu said *"No moves are permitted from Done. An
 * admin can add one in Settings → Transitions."* — a sentence that was false the moment the API
 * stopped consulting that table, and that sent the reader to configure something which decides
 * nothing.
 *
 * ⭐⭐ THE SUGGESTION SURVIVES AS AN ORDER, not as a filter. `suggested` is offered first under its
 * own heading; every other ACTIVE status follows. ⛔ Nothing is disabled for not being suggested.
 */
export interface StatusMenu {
  /** What the API says normally comes next, in its order. */
  suggested: Transition[];
  /** Every OTHER active status — empty when the server is not advisory. */
  others: Transition[];
  /** `transitions_are_advisory` off the wire. `false` until the server says otherwise. */
  advisory: boolean;
}

export function buildMenu(
  advice: TransitionAdvice | null,
  statuses: readonly StatusDef[],
  currentStatus: string,
): StatusMenu {
  const advisory = advice?.advisory === true;
  const suggested = (advice?.suggested ?? []).filter((entry) => entry.to_status !== currentStatus);
  const offered = new Set(suggested.map((entry) => entry.to_status));

  /**
   * ⛔ THE CURRENT STATUS IS NEVER IN THE MENU. `performTransition` answers 409 `ticket is already
   * in status 'x'` and refuses to write a history row recording that nothing happened.
   * ⛔ NOR IS AN INACTIVE ONE: `is_active = false` means "not offered as somewhere new to go", and
   * `assertTargetIsUsable` answers 409 `is a retired status and cannot be moved into`.
   * ⛔ AND `others` IS EMPTY WHEN THE SERVER IS NOT ADVISORY. A deployment older than 2026-08-17
   * still enforces the graph; offering every status there would fill the menu with options that 409.
   */
  const others = advisory
    ? statuses
        .filter(
          (status) => status.is_active && status.code !== currentStatus && !offered.has(status.code),
        )
        .map((status) => ({
          to_status: status.code,
          label: status.label,
          colour: status.colour,
          note: null,
        }))
    : [];

  return { suggested, others, advisory };
}
